import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import { Autocomplete, Box, Chip, FilterOptionsState, TextField, Typography } from '@mui/material';

import { ProjectListResult } from 'stores/ProjectStoreAdapter';

// --- 共通Props ---

type ProjectSelectBaseProps = {
  /** 全プロジェクトリスト */
  projects: ProjectListResult[];
  /** ラベル */
  label?: string;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** 必須表示 */
  required?: boolean;
};

// --- 複数選択Props ---

type ProjectSelectMultiProps = ProjectSelectBaseProps & {
  /** 選択モード: 複数 */
  mode: 'multi';
  /** 選択中のプロジェクトIDリスト */
  selectedProjectIds: string[];
  /** 選択変更ハンドラ（複数） */
  onChange: (projectIds: string[]) => void;
};

// --- 単一選択Props ---

type ProjectSelectSingleProps = ProjectSelectBaseProps & {
  /** 選択モード: 単一 */
  mode: 'single';
  /** 選択中のプロジェクトID（null=未選択） */
  selectedProjectId: string | null;
  /** 選択変更ハンドラ（単一） */
  onChange: (projectId: string | null) => void;
};

export type ProjectSelectProps = ProjectSelectMultiProps | ProjectSelectSingleProps;

/**
 * プロジェクト選択コンポーネント
 *
 * Autocomplete ベースでプロジェクトを検索・選択可能。
 * mode="multi" で複数選択、mode="single" で単一選択。
 * プロジェクト名・カナで絞り込みができる。
 */
const ProjectSelect: React.FC<ProjectSelectProps> = (props) => {
  const {
    projects,
    label = 'プロジェクト',
    error = false,
    helperText,
    disabled = false,
    required = false,
  } = props;

  const filterOptions = (
    options: ProjectListResult[],
    state: FilterOptionsState<ProjectListResult>,
  ) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;
    return options.filter(
      (p) =>
        p.projectName.toLowerCase().includes(input) ||
        p.projectNameKana.toLowerCase().includes(input),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: ProjectListResult,
  ) => {
    const { key, ...rest } = optionProps;
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <FolderIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.projectName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {option.clientName}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (props.mode === 'single') {
    const selectedProject = projects.find((p) => p.projectId === props.selectedProjectId) || null;

    return (
      <Autocomplete
        options={projects}
        value={selectedProject}
        onChange={(_, newValue) => props.onChange(newValue?.projectId || null)}
        getOptionLabel={(option) => option.projectName}
        filterOptions={filterOptions}
        isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            required={required}
            error={error}
            helperText={helperText}
            placeholder="プロジェクトを検索..."
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        )}
        renderOption={renderOption}
        noOptionsText="該当するプロジェクトがありません"
        sx={{ width: '100%' }}
      />
    );
  }

  // mode === 'multi'
  const selectedProjects = projects.filter((p) => props.selectedProjectIds.includes(p.projectId));

  return (
    <Autocomplete
      multiple
      options={projects}
      value={selectedProjects}
      onChange={(_, newValue) => props.onChange(newValue.map((p) => p.projectId))}
      getOptionLabel={(option) => option.projectName}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, value) => option.projectId === value.projectId}
      disabled={disabled}
      disableCloseOnSelect
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required={required}
          error={error}
          helperText={helperText}
          placeholder={selectedProjects.length === 0 ? 'プロジェクトを検索...' : ''}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      renderTags={(value, getTagProps) =>
        value.map((project, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...tagProps}
              label={project.projectName}
              size="small"
              icon={<FolderIcon sx={{ fontSize: 16 }} />}
              deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
              sx={{
                borderRadius: 1.5,
                fontWeight: 500,
                '& .MuiChip-icon': { color: 'primary.main' },
              }}
            />
          );
        })
      }
      noOptionsText="該当するプロジェクトがありません"
      sx={{ width: '100%' }}
    />
  );
};

export default ProjectSelect;
